// Refetching an imported recipe from its link (the recipe page's "Refresh
// from link"): the page fills in what the saved recipe lacks and never
// overwrites what is there, so hand edits survive. Tags, star and the
// vegetarian choice are the household's and never come from the page; the
// page's photo only fills a recipe that has none (useRecipes.refreshRecipe).

import type { Recipe } from './supabase'
import type { NewRecipe } from './recipeImport'

export type RefreshField = 'servings' | 'minutes' | 'ingredients' | 'steps'
/** What a refresh can add: the fields, and a photo when the recipe had none. */
export type RefreshAdded = RefreshField | 'photo'
export type RefreshPatch = Partial<Pick<Recipe, RefreshField>>

const LABELS: Record<RefreshAdded, string> = {
  servings: 'servings',
  minutes: 'the time',
  ingredients: 'the ingredients',
  steps: 'the method',
  photo: 'the photo',
}

/** What the fetched page adds to the saved recipe: only fields the recipe has empty. */
export function fillMissing(saved: Pick<Recipe, RefreshField>, page: Pick<NewRecipe, RefreshField>): { patch: RefreshPatch; filled: RefreshField[] } {
  const patch: RefreshPatch = {}
  if (saved.servings == null && page.servings != null) patch.servings = page.servings
  if (saved.minutes == null && page.minutes != null) patch.minutes = page.minutes
  if (saved.ingredients.length === 0 && page.ingredients.length > 0) patch.ingredients = page.ingredients
  if (saved.steps.length === 0 && page.steps.length > 0) patch.steps = page.steps
  return { patch, filled: Object.keys(patch) as RefreshField[] }
}

/** "Added the method and the time." / "Nothing new on the page." */
export function refreshSummary(filled: RefreshAdded[]): string {
  if (filled.length === 0) return 'Nothing new on the page. Your recipe already has everything it lists.'
  const parts = filled.map(f => LABELS[f])
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  return `Added ${list} from the page.`
}
